import { ObservationControllerService } from "@9amhealth/openapi";
import {
  CalendarDateTime,
  DateDuration,
  DateTimeDuration,
  DateValue,
  endOfMonth,
  endOfWeek,
  getLocalTimeZone,
  now,
  parseDateTime,
  startOfMonth,
  startOfWeek,
  toCalendarDate,
  today
} from "@internationalized/date";
import { Cubit } from "blac-next";
import { Observation } from "fhir/r4";
import React, { ReactNode } from "react";
import { LoincCodingCode } from "src/constants/fhir";
import {
  END_OF_DAY,
  END_OF_HOUR,
  START_OF_DAY,
  START_OF_HOUR
} from "src/constants/misc";
import { parseDateMultiTry } from "src/lib/date";
import Fhir from "src/lib/fhir";
import { getSupportedUserLanguage } from "src/lib/i18next";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import { cmToInch, kgToPounds } from "src/lib/unitConversion";
import {
  memStorage,
  StorageController
} from "src/state/StorageBloc/StorageBloc";

type DataValueCalculation = "average" | "single";

export type ChartDataItem = {
  primaryValue: number;
  secondaryValue?: number;
  unit: string;
  time: number;
  date: DateValue;
  valueCalculation: DataValueCalculation;
  label?: string;
  valueString?: string;
  /** Raw data points used to calculate this point (only for averaged points) */
  rawDataPoints?: ChartDataItem[];
};

export type ChartDateItemPrimitive = Omit<ChartDataItem, "date"> & {
  date: string;
};

type DateRange = {
  fromDate: DateValue;
  toDate: DateValue;
};

export type Progress = {
  difference?: number;
  secondaryDifference?: number;
  unit?: string;
};

type ChartTimeRange = {
  domain: DateTimeDuration;
  /** Label for the time range */
  label: DateTimeDuration;
  /** Average values in this interval, default is undefined which will not average out values */
  average?: DateTimeDuration;
};

interface DataHubState<T> {
  activeLoincCode?: LoincCodingCode;
  activeTimeRange?: ChartTimeRange;
  availableTimeRanges?: ChartTimeRange[];
  activeProgress?: Progress;
  /** Chart data, undefined means that its loading */
  chartData?: T[];
  chartLabel?: {
    domain: number[];
    ticks: number[];
  };
}

export const DataHubAvailableLoincCodes: LoincCodingCode[] = [
  LoincCodingCode.weight,
  LoincCodingCode.waistCircumference,
  LoincCodingCode.stepsInDay,
  LoincCodingCode.hba1c,
  LoincCodingCode.bloodPressure,
  // the rest are not yet implemented
  LoincCodingCode.bloodGlucoseCapillary
];

export const LoincCodeGroups: Partial<
  Record<LoincCodingCode, LoincCodingCode[]>
> = {
  [LoincCodingCode.bloodGlucoseCapillary]: [
    LoincCodingCode.bloodGlucoseCapillary,
    LoincCodingCode.bloodGlucoseCapillaryFasting
  ]
};

const LoincCodeToTimeRangesRecord: Partial<
  Record<LoincCodingCode, ChartTimeRange[]>
> = {
  [LoincCodingCode.hba1c]: [
    { domain: { months: 3 }, average: { weeks: 1 }, label: { weeks: 1 } },
    { domain: { months: 6 }, average: { weeks: 1 }, label: { months: 1 } },
    { domain: { years: 1 }, average: { months: 1 }, label: { months: 1 } }
  ],
  [LoincCodingCode.weight]: [
    { domain: { weeks: 1 }, label: { days: 1 }, average: { hours: 1 } },
    { domain: { months: 3 }, average: { weeks: 1 }, label: { weeks: 1 } },
    { domain: { months: 6 }, average: { weeks: 1 }, label: { months: 1 } },
    { domain: { years: 1 }, average: { months: 1 }, label: { months: 1 } }
  ],
  [LoincCodingCode.waistCircumference]: [
    { domain: { weeks: 1 }, label: { days: 1 } },
    { domain: { months: 3 }, average: { weeks: 1 }, label: { weeks: 1 } },
    { domain: { months: 6 }, average: { weeks: 1 }, label: { months: 1 } },
    { domain: { years: 1 }, average: { months: 1 }, label: { months: 1 } }
  ],
  [LoincCodingCode.stepsInDay]: [
    { domain: { weeks: 1 }, average: { days: 1 }, label: { days: 1 } },
    { domain: { months: 3 }, average: { weeks: 1 }, label: { weeks: 1 } },
    { domain: { months: 6 }, average: { weeks: 1 }, label: { months: 1 } },
    { domain: { years: 1 }, average: { months: 1 }, label: { months: 1 } }
  ],
  [LoincCodingCode.bloodPressure]: [
    { domain: { weeks: 1 }, label: { days: 1 } },
    { domain: { months: 3 }, average: { weeks: 1 }, label: { weeks: 1 } },
    { domain: { months: 6 }, average: { weeks: 1 }, label: { months: 1 } },
    { domain: { years: 1 }, average: { months: 1 }, label: { months: 1 } }
  ],
  [LoincCodingCode.bloodGlucoseCapillary]: [
    { domain: { days: 1 }, label: { hours: 1 }, average: { hours: 1 } },
    { domain: { weeks: 1 }, label: { days: 1 }, average: { days: 1 } },
    { domain: { months: 3 }, average: { weeks: 1 }, label: { weeks: 1 } },
    { domain: { months: 6 }, average: { weeks: 1 }, label: { months: 1 } },
    { domain: { years: 1 }, average: { months: 1 }, label: { months: 1 } }
  ]
};

export default class DataHubCubit extends Cubit<DataHubState<ChartDataItem>> {
  static isolated = false;

  private storageKeys = {
    activeLoincCode: "activeLoincCode"
  };
  private requestCounter = 0;
  /** Current request ID to handle race conditions */
  private currentRequestId?: number;
  public activeObservations: ChartDataItem[] = [];
  public displayTimezone = getLocalTimeZone();
  public allDataFromDate = this.now.subtract({ months: 14 });
  public availableCharts: LoincCodingCode[] = [
    // first in list is active by default
    LoincCodingCode.weight,
    LoincCodingCode.hba1c,
    LoincCodingCode.waistCircumference,
    LoincCodingCode.stepsInDay,
    LoincCodingCode.bloodPressure,
    LoincCodingCode.bloodGlucoseCapillary
  ];

  constructor() {
    super({
      chartData: [],
      chartLabel: {
        domain: [],
        ticks: []
      }
    });

    void this.setActiveObservations({
      loincCode: this.initialState.loincCode
    });
  }

  get now() {
    return now(getLocalTimeZone());
  }

  get initialState(): {
    loincCode?: LoincCodingCode;
    activeTimeRange?: ChartTimeRange;
  } {
    const state = {
      loincCode: this.availableCharts[0],
      activeTimeRange: LoincCodeToTimeRangesRecord[this.availableCharts[0]]?.[0]
    };

    const cachedLoincCode = StorageController.getItem(
      this.storageKeys.activeLoincCode,
      sessionStorage
    );

    if (cachedLoincCode) {
      state.loincCode = cachedLoincCode as LoincCodingCode;
    }

    return state;
  }

  get userLanguage() {
    return getSupportedUserLanguage();
  }

  private dateStartOfLabel = (date: DateValue): DateValue => {
    const { label } = this.state.activeTimeRange ?? {};
    if (!label) {
      return date;
    }

    if (label.months === 1) {
      return startOfMonth(date);
    }

    if (label.weeks === 1) {
      return startOfWeek(date, "en-US");
    }

    if (label.days === 1) {
      return date.copy().set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    }

    if (label.hours === 1) {
      return date.copy().set({ minute: 0, second: 0, millisecond: 0 });
    }

    // eslint-disable-next-line no-console
    console.error("Cannot find start for label", {
      label: this.state.activeTimeRange?.label
    });
    return date;
  };

  private dateLimitOfAverage = (
    date: DateValue,
    side: "end" | "start"
  ): DateValue => {
    const { average } = this.state.activeTimeRange ?? {};
    if (!average) {
      return date;
    }

    let startofLimit: null | ((date: DateValue) => DateValue) = null;
    let endOfLimit: null | ((date: DateValue) => DateValue) = null;

    if (average.months === 1) {
      endOfLimit = (date: DateValue) => endOfMonth(date).set(END_OF_DAY);
      startofLimit = (date: DateValue) => startOfMonth(date);
    }

    if (average.weeks === 1) {
      endOfLimit = (date: DateValue) =>
        endOfWeek(date, "en-US").set(END_OF_DAY);
      startofLimit = (date: DateValue) => startOfWeek(date, "en-US");
    }

    if (average.days === 1) {
      endOfLimit = (date: DateValue) => date.set(END_OF_DAY);
      startofLimit = (date: DateValue) => date.set(START_OF_DAY);
    }

    if (average.hours === 1) {
      endOfLimit = (date: DateValue) => date.set(END_OF_HOUR);
      startofLimit = (date: DateValue) => date.set(START_OF_HOUR);
    }

    if (startofLimit && endOfLimit) {
      return side === "start" ? startofLimit(date) : endOfLimit(date);
    }

    return date;
  };

  private readonly getTimeRangeDates = (): DateRange => {
    const { domain } = this.state.activeTimeRange ?? {};

    const isAvailable = this.state.activeLoincCode
      ? this.availableCharts.includes(this.state.activeLoincCode)
      : false;

    if (!domain && isAvailable) {
      throw new Error("Active time range is not set");
    }

    const fallbackDomain = {
      months: 3
    };

    const fromDate = this.dateLimitOfAverage(
      this.now.subtract(domain ?? fallbackDomain),
      "start"
    );

    return {
      fromDate,
      toDate: this.now
    };
  };

  public prepareDataItemForCache = (
    item: ChartDataItem
  ): ChartDateItemPrimitive => {
    return {
      ...item,
      date: item.date.toString()
    };
  };

  public parseItemFromCache = (item: ChartDateItemPrimitive): ChartDataItem => {
    return {
      ...item,
      date: parseDateTime(item.date)
    };
  };

  cachedKeys = new Set<string>();
  public clearApiCache = (loincCode?: LoincCodingCode) => {
    for (const key of this.cachedKeys) {
      // if a loinc code is set, only remove keys that include the loinc code
      if (loincCode && !key.includes(loincCode)) {
        continue;
      }

      StorageController.removeItem(key, memStorage);
    }
    this.cachedKeys.clear();
  };

  public readonly fetchObservations = async (options: {
    loincCodes: LoincCodingCode[];
    toDate: DateValue;
    forceRefresh?: boolean;
  }): Promise<ChartDataItem[]> => {
    // load from cache
    const day = today(getLocalTimeZone());
    const cacheKey = `datahub:${day.toString()}:${options.loincCodes.join("-")}`;
    const cached = StorageController.getItemParsed<ChartDateItemPrimitive[]>(
      cacheKey,
      memStorage
    );
    if (cached && cached.length > 0 && !options.forceRefresh) {
      return cached.map(this.parseItemFromCache);
    }

    // fetch from server
    const response = await ObservationControllerService.getObservations(
      toCalendarDate(this.allDataFromDate).toString(),
      toCalendarDate(options.toDate).toString(),
      options.loincCodes
    );

    const { data } = response as unknown as {
      status: string;
      data: Observation[];
    };

    // prepare data for charts
    const customTypedObservations: ChartDataItem[] = data
      .map(this.parseObservationToChartDataItem)
      .filter((dataItem) => dataItem !== null) as ChartDataItem[];

    const sortedObservations = customTypedObservations.toSorted((a, b) =>
      a.date.compare(b.date)
    );

    // save to cache
    StorageController.setItem(
      cacheKey,
      JSON.stringify(sortedObservations.map(this.prepareDataItemForCache)),
      memStorage
    );
    this.cachedKeys.add(cacheKey);

    return sortedObservations;
  };

  readonly setActiveObservations = async (options: {
    loincCode?: LoincCodingCode;
    forceRefresh?: boolean;
  }) => {
    if (!options.loincCode) {
      throw new Error("Loinc code is not set");
    }

    const requestId = ++this.requestCounter;

    const currentTimeRange = this.state.activeTimeRange;

    let activeTimeRange = LoincCodeToTimeRangesRecord[options.loincCode]?.[0];

    // if no time ranges are available for the current loinc code, use the current one
    if (!activeTimeRange) {
      activeTimeRange = currentTimeRange;
    }

    this.patch({
      activeLoincCode: options.loincCode,
      activeTimeRange,
      chartData: undefined,
      chartLabel: undefined
    });

    this.currentRequestId = requestId;

    this.activeObservations = [];

    StorageController.setItem(
      this.storageKeys.activeLoincCode,
      options.loincCode,
      sessionStorage
    );

    try {
      this.setAvailableTimeRanges();
      const { toDate } = this.getTimeRangeDates();
      const loincCodeGroup = LoincCodeGroups[options.loincCode];

      const observations = await this.fetchObservations({
        loincCodes: loincCodeGroup ?? [options.loincCode],
        toDate,
        forceRefresh: options.forceRefresh
      });

      // Only update state if this is still the most recent request
      if (requestId === this.currentRequestId) {
        this.activeObservations = observations;
        this.setChartData();
        this.setAvailableTimeRanges();
        this.setActiveProgress();
      }
    } catch (error) {
      reportErrorSentry(error);
    }
  };

  private readonly parseBpObservationToChartDataItem = (
    obs: Observation,
    date: CalendarDateTime
  ): ChartDataItem | null => {
    const systolic = obs.component?.find(
      (c) => c.code.coding?.[0]?.code === LoincCodingCode.bloodPressureSystolic
    );

    const diastolic = obs.component?.find(
      (c) => c.code.coding?.[0]?.code === LoincCodingCode.bloodPressureDiastolic
    );

    if (
      !systolic?.valueQuantity?.value ||
      !diastolic?.valueQuantity?.value ||
      diastolic.valueQuantity.unit !== systolic.valueQuantity.unit
    ) {
      return null;
    }

    return {
      primaryValue: systolic.valueQuantity.value,
      secondaryValue: diastolic.valueQuantity.value,
      time: date.toDate(this.displayTimezone).getTime(),
      valueCalculation: "single",
      unit: systolic.valueQuantity.unit ?? "",
      date
    } satisfies ChartDataItem;
  };

  public parseObservationToChartDataItem = (
    obs: Observation
  ): ChartDataItem | null => {
    const date =
      obs.effectiveDateTime && parseDateMultiTry(obs.effectiveDateTime);
    const isBpObservation =
      obs.code.coding?.[0]?.code === LoincCodingCode.bloodPressure;

    // remove observations with no date
    if (!date) {
      return null;
    }

    if (isBpObservation) {
      const bpChartItem = this.parseBpObservationToChartDataItem(obs, date);
      return bpChartItem;
    }

    const value = obs.valueQuantity?.value;
    if (typeof value !== "number") {
      return null;
    }

    let convertedValue = value;
    let unit = obs.valueQuantity?.unit ?? "";

    // convert units
    if (unit === "kg") {
      convertedValue = parseFloat(kgToPounds(value).toFixed(1));
      unit = translate("lbs");
    }

    if (unit === "cm") {
      const loincCoding = Fhir.getLoincCoding(obs.code.coding);
      const code = loincCoding?.code;
      if (code === LoincCodingCode.waistCircumference) {
        convertedValue = parseFloat(cmToInch(value).toFixed(1));
      } else {
        convertedValue = Math.round(cmToInch(value));
      }
      unit = translate("inches");
    }

    return {
      primaryValue: convertedValue,
      time: date.toDate(this.displayTimezone).getTime(),
      valueCalculation: "single",
      unit,
      date
    } satisfies ChartDataItem;
  };

  public setChartData = () => {
    const values = this.activeObservations;
    const averaged = this.groupChartDataByTimeRangeAverage(values);
    const label = this.generateLabels();

    this.patch({
      chartData: averaged,
      chartLabel: {
        domain: label.domain,
        ticks: label.ticks
      }
    });
  };

  getSampleValue = (values: ChartDataItem[]): ChartDataItem => {
    if (values.length === 0) {
      return {
        primaryValue: 0,
        unit: "",
        time: 0,
        date: now(getLocalTimeZone()),
        valueCalculation: "single"
      };
    }
    return values[0];
  };

  private readonly setAvailableTimeRanges = () => {
    if (!this.state.activeLoincCode) {
      throw new Error("Active loinc code is not set");
    }

    this.patch({
      availableTimeRanges:
        LoincCodeToTimeRangesRecord[this.state.activeLoincCode]
    });
  };

  private readonly setActiveProgress = () => {
    const { toDate, fromDate } = this.getTimeRangeDates();

    const chartData = this.groupChartDataByTimeRangeAverage(
      this.activeObservations,
      fromDate,
      toDate
    );

    if (chartData.length < 2) {
      this.patch({
        activeProgress: undefined
      });

      return;
    }

    const { unit } = this.getSampleValue(chartData);
    const firstChartValue = chartData[0].primaryValue;
    const lastChartValue = chartData[chartData.length - 1].primaryValue;

    const firstSecondaryValue = chartData[0].secondaryValue;
    const lastSecondaryValue = chartData[chartData.length - 1].secondaryValue;

    let difference: number | undefined = lastChartValue - firstChartValue;
    const secondaryDifference =
      lastSecondaryValue && firstSecondaryValue
        ? lastSecondaryValue - firstSecondaryValue
        : undefined;

    if (unit === "steps") {
      const positiveChartData = chartData.filter((obs) => obs.primaryValue > 0);

      const positiveChartDataSum = positiveChartData.reduce(
        (acc, val) => acc + val.primaryValue,
        0
      );
      difference = positiveChartDataSum / positiveChartData.length;
    }

    this.patch({
      activeProgress: {
        difference,
        secondaryDifference,
        unit
      }
    });
  };

  public readonly isSameTimeRange = (
    timeRange: DateDuration,
    activeTimeRange?: DateDuration
  ) => {
    const sameYears = activeTimeRange?.years === timeRange.years;
    const sameMonths = activeTimeRange?.months === timeRange.months;
    const sameWeeks = activeTimeRange?.weeks === timeRange.weeks;
    const sameDays = activeTimeRange?.days === timeRange.days;

    return sameYears && sameMonths && sameWeeks && sameDays;
  };

  public readonly setActiveInterval = (interval: DateDuration) => {
    const activeTimeRange = this.state.availableTimeRanges?.find(
      (availableTimeRange) => availableTimeRange.domain === interval
    );
    if (!activeTimeRange) {
      throw new Error("Active time range is not found");
    }

    this.patch({
      activeTimeRange,
      activeProgress: undefined
    });
    this.setActiveProgress();
    this.setChartData();
  };

  getValuesInDateRange = (
    startDate: DateValue,
    endDate: DateValue,
    values: ChartDataItem[]
  ): {
    primaryAverageValue: number | null;
    valuesInRange: ChartDataItem[];
    secondaryAverageValue?: number | null;
  } => {
    const valuesInRange: ChartDataItem[] = [];
    for (const v of values) {
      if (
        typeof v.date === "object" &&
        typeof v.primaryValue === "number" &&
        v.date.compare(startDate) >= 0 &&
        v.date.compare(endDate) <= 0
      ) {
        valuesInRange.push(v);
      }
    }

    const total = valuesInRange.reduce((acc, v) => acc + v.primaryValue, 0);
    const secondaryTotal = valuesInRange.some(
      (v) => v.secondaryValue === undefined
    )
      ? undefined
      : valuesInRange.reduce((acc, v) => acc + (v.secondaryValue ?? 0), 0);
    let primaryAverageValue =
      valuesInRange.length > 0 ? total / valuesInRange.length : null;
    let secondaryAverageValue =
      secondaryTotal !== undefined && valuesInRange.length > 0
        ? secondaryTotal / valuesInRange.length
        : null;
    if (primaryAverageValue !== null) {
      // limit to 5 decimal places, this also avoids floating point errors
      primaryAverageValue = parseFloat(primaryAverageValue.toFixed(5));
    }

    if (secondaryAverageValue !== null) {
      // limit to 5 decimal places, this also avoids floating point errors
      secondaryAverageValue = parseFloat(secondaryAverageValue.toFixed(5));
    }

    return {
      primaryAverageValue,
      secondaryAverageValue,
      valuesInRange
    };
  };

  public groupChartDataByTimeRangeAverage = (
    values: ChartDataItem[],
    startDate: DateValue = this.allDataFromDate,
    endDate: DateValue = this.dateLimitOfAverage(this.now, "end")
  ): ChartDataItem[] => {
    const array: ChartDataItem[] = [];
    const { average } = this.state.activeTimeRange ?? {};

    if (!average) {
      return this.getValuesInDateRange(startDate, endDate, values)
        .valuesInRange;
    }

    let date = this.dateLimitOfAverage(startDate, "start");
    const { unit } = this.getSampleValue(values);
    if (average.months === 1 || average.weeks === 1 || average.days === 1) {
      date = date.set({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0
      });
    } else if (average.hours === 1) {
      date = date.set({ minute: 0, second: 0, millisecond: 0 });
    }

    while (date.compare(endDate) <= 0) {
      let nextDate = this.dateLimitOfAverage(date, "end");
      nextDate = endDate.compare(nextDate) < 0 ? endDate : nextDate;

      const { primaryAverageValue, valuesInRange, secondaryAverageValue } =
        this.getValuesInDateRange(date, nextDate, values);

      if (primaryAverageValue !== null) {
        const obj: ChartDataItem = {
          primaryValue: primaryAverageValue,
          unit: unit,
          valueCalculation: valuesInRange.length > 1 ? "average" : "single",
          time: date.toDate(this.displayTimezone).getTime(),
          date: date,
          // Include raw data points for averaged values
          rawDataPoints: valuesInRange.length > 1 ? valuesInRange : undefined
        };
        if (secondaryAverageValue) {
          obj.secondaryValue = secondaryAverageValue;
        }
        array.push(obj);
      }

      date = date.add(average);
    }

    return array;
  };

  generateLabels = () => {
    const timestamps = [];
    const { fromDate, toDate } = this.getTimeRangeDates();

    let countStartDate = this.dateStartOfLabel(fromDate);
    const { label } = this.state.activeTimeRange ?? { label: { months: 1 } };
    let maxTicks = 50;

    while (countStartDate.compare(this.now) <= 0 && maxTicks > 0) {
      timestamps.push(countStartDate.toDate(this.displayTimezone).getTime());
      countStartDate = this.dateStartOfLabel(countStartDate.add(label));
      maxTicks -= 1;
    }

    return {
      domain: [
        fromDate.toDate(this.displayTimezone).getTime(),
        toDate.toDate(this.displayTimezone).getTime()
      ],
      ticks: timestamps
    };
  };

  public formatValueDataStatus = (value: number, unit: string) => {
    const isPositive = value > 0;
    const sign = isPositive && unit !== "steps" ? "+" : "";
    const decimalPlaces = unit === "steps" ? 0 : 1;
    const formattedValue = `${sign}${value.toFixed(decimalPlaces)}`;
    return decimalPlaces === 1 && unit !== "lbs"
      ? formattedValue.replace(".0", "")
      : formattedValue;
  };

  public tickFormatterAxisX = (time: number) => {
    if (!this.state.activeTimeRange) {
      throw new Error("Active time range is not set");
    }
    const tickCount = this.state.chartLabel?.ticks.length ?? 0;
    const { months, weeks, days, hours } = this.state.activeTimeRange.label;

    if (months === 1) {
      if (tickCount <= 7) {
        return new Date(time).toLocaleDateString(this.userLanguage, {
          month: "short"
        });
      } else {
        return new Date(time)
          .toLocaleDateString(this.userLanguage, {
            month: "short"
          })
          .slice(0, 1);
      }
    }

    if (weeks === 1) {
      return new Date(time).toLocaleDateString(this.userLanguage, {
        month: "short",
        day: "numeric"
      });
    }

    if (days === 1) {
      return new Date(time).toLocaleDateString(this.userLanguage, {
        day: "numeric"
      });
    }

    if (hours === 1) {
      return new Date(time).toLocaleTimeString(this.userLanguage, {
        hour: "numeric"
      });
    }

    return "";
  };

  public formatLabel = ({ payload }: { payload: ChartDataItem[] }) => {
    // eslint-disable-next-line @typescript-eslint/prefer-destructuring
    const { date, label } = payload[0];
    if (label) {
      return label;
    }
    const { average } = this.state.activeTimeRange ?? {};

    if (average?.months === 1) {
      return date
        .toDate(this.displayTimezone)
        .toLocaleDateString(this.userLanguage, { month: "short" });
    }

    if (average?.weeks === 1) {
      const from = date
        .toDate(this.displayTimezone)
        .toLocaleDateString(this.userLanguage, {
          month: "short",
          day: "numeric"
        });
      const to = date
        .add({ days: 6 })
        .toDate(this.displayTimezone)
        .toLocaleDateString(this.userLanguage, {
          month: "short",
          day: "numeric"
        });
      return translate("chart.label.week", { from, to });
    }

    if (average?.days === 1) {
      return date
        .toDate(this.displayTimezone)
        .toLocaleDateString(this.userLanguage, {
          month: "short",
          day: "numeric",
          hour: "numeric"
        });
    }

    if (average?.hours === 1) {
      return date
        .toDate(this.displayTimezone)
        .toLocaleDateString(this.userLanguage, {
          month: "short",
          day: "numeric",
          hour: "numeric"
        });
    }

    return date
      .toDate(this.displayTimezone)
      .toLocaleDateString(this.userLanguage, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "numeric"
      });
  };

  public formatValueRemoveZeroDecimals = (value: number) => {
    return value.toFixed(1).replace(/\.0$/, "");
  };

  public formatValue = ({
    payload
  }: {
    payload: ChartDataItem[];
  }): ReactNode => {
    const [
      { primaryValue, secondaryValue, valueCalculation, valueString, unit }
    ] = payload;

    if (valueString) {
      return valueString;
    }

    let formattedValue = primaryValue.toString();
    let formattedValue2 = secondaryValue?.toString();

    if (
      unit === translate("lbs") ||
      unit === translate("inches") ||
      unit === translate("mgdL") ||
      unit === translate("steps")
    ) {
      formattedValue = this.formatValueRemoveZeroDecimals(primaryValue);
    }

    if (unit === translate("mmHg")) {
      formattedValue = primaryValue.toFixed(0);
      formattedValue2 = secondaryValue?.toFixed(0);
    }

    if (valueCalculation === "average") {
      return (
        <span>
          {formattedValue}
          {secondaryValue ? `/${formattedValue2}` : ""} {unit}{" "}
          <em>{translate(`chart.average`)}</em>
        </span>
      );
    }

    return `${formattedValue} ${secondaryValue ? `/${formattedValue2}` : ""} ${unit}`;
  };

  public readonly getLoincCodesForUrl = (
    loincCode?: LoincCodingCode
  ): string => {
    if (!loincCode) {
      return "";
    }

    const loincCodeGroup = LoincCodeGroups[loincCode];

    return loincCodeGroup ? loincCodeGroup.join(",") : loincCode;
  };
}
